// Placeholder sound design with the Web Audio API: an engine drone and short synthesized blips, so the
// game has feedback before the generated ElevenLabs/Suno assets land. Everything is muted until the
// first user gesture unlocks the context.

import { ASSET_BASE } from './config';

const CLIPS = ['horn', 'crash_small', 'crash_big', 'explode', 'respawn', 'pickup', 'cash', 'tick', 'fail', 'go', 'card', 'select', 'reroll', 'siren', 'fine', 'alarm', 'tsunami', 'earthquake', 'volcano', 'cyclone', 'rock_hit', 'splash', 'ped_yelp', 'nitro', 'jump', 'ghost', 'freeze', 'rush', 'timesup', 'win', 'skid'];

export class Sfx {
  private ctx: AudioContext | null = null;
  private buffers = new Map<string, AudioBuffer>();
  private loading = new Set<string>();
  private music: { src: AudioBufferSourceNode; gain: GainNode; name: string } | null = null;
  private lastPlay = new Map<string, number>();
  private engineOsc: OscillatorNode | null = null;
  private engineGain: GainNode | null = null;
  private master: GainNode | null = null;
  muted = false;

  constructor() {
    const unlock = () => { this.ensure(); window.removeEventListener('pointerdown', unlock); window.removeEventListener('keydown', unlock); };
    window.addEventListener('pointerdown', unlock); window.addEventListener('keydown', unlock);
    try { this.muted = localStorage.getItem('cab.muted') === '1'; } catch { /* ignore */ }
  }

  private ensure(): AudioContext | null {
    if (this.ctx) { if (this.ctx.state === 'suspended') void this.ctx.resume(); return this.ctx; }
    try {
      const AC = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      this.ctx = new AC();
      this.master = this.ctx.createGain(); this.master.gain.value = this.muted ? 0 : 0.6; this.master.connect(this.ctx.destination);
      this.engineOsc = this.ctx.createOscillator(); this.engineOsc.type = 'sawtooth'; this.engineOsc.frequency.value = 60;
      const filter = this.ctx.createBiquadFilter(); filter.type = 'lowpass'; filter.frequency.value = 400;
      this.engineGain = this.ctx.createGain(); this.engineGain.gain.value = 0.0;
      this.engineOsc.connect(filter); filter.connect(this.engineGain); this.engineGain.connect(this.master); this.engineOsc.start();
    } catch { this.ctx = null; }
    return this.ctx;
  }

  /** Play a generated clip by name (sfx_<name>.mp3 / vo_<name>.mp3); returns false when it is not available. */
  play(name: string, gain = 0.8, minGapMs = 80): boolean {
    const ctx = this.ensure(); if (!ctx || !this.master) return false;
    const key = name.startsWith('vo_') ? name : `sfx_${name}`;
    const buf = this.buffers.get(key);
    if (!buf) { this.load(key); return false; }
    const now = performance.now(); if (now - (this.lastPlay.get(key) ?? 0) < minGapMs) return true; this.lastPlay.set(key, now);
    const src = ctx.createBufferSource(); src.buffer = buf; const g = ctx.createGain(); g.gain.value = gain; src.connect(g); g.connect(this.master); src.start();
    return true;
  }
  private load(key: string): void {
    if (this.loading.has(key) || this.buffers.has(key) || !this.ctx) return;
    this.loading.add(key);
    fetch(`${ASSET_BASE}audio/${key}.mp3`).then((r) => (r.ok ? r.arrayBuffer() : Promise.reject(new Error(String(r.status))))).then((ab) => this.ctx!.decodeAudioData(ab)).then((b) => this.buffers.set(key, b)).catch(() => { /* not generated yet: synth fallback */ });
  }
  /** Warm the cache for everything we might play. */
  preload(): void { this.ensure(); for (const c of CLIPS) this.load(`sfx_${c}`); }
  say(line: string, gain = 1): boolean { return this.play(line, gain, 500); }
  /** Loop a music track (music_<name>.mp3) with a short crossfade; silently does nothing without the file. */
  playMusic(name: string, gain = 0.35): void {
    const ctx = this.ensure(); if (!ctx || !this.master) return;
    if (this.music?.name === name) return;
    const start = (buf: AudioBuffer) => {
      if (this.music) { const old = this.music; old.gain.gain.setTargetAtTime(0, ctx.currentTime, 0.4); setTimeout(() => old.src.stop(), 1500); }
      const src = ctx.createBufferSource(); src.buffer = buf; src.loop = true; const g = ctx.createGain(); g.gain.value = 0; src.connect(g); g.connect(this.master!); src.start(); g.gain.setTargetAtTime(gain, ctx.currentTime, 0.6);
      this.music = { src, gain: g, name };
    };
    const key = `music_${name}`; const have = this.buffers.get(key);
    if (have) start(have);
    else fetch(`${ASSET_BASE}audio/${key}.mp3`).then((r) => (r.ok ? r.arrayBuffer() : Promise.reject(new Error(String(r.status))))).then((ab) => ctx.decodeAudioData(ab)).then((b) => { this.buffers.set(key, b); if (!this.music || this.music.name !== name) start(b); }).catch(() => {});
  }
  stopMusic(): void { if (this.music && this.ctx) { const old = this.music; old.gain.gain.setTargetAtTime(0, this.ctx.currentTime, 0.4); setTimeout(() => old.src.stop(), 1500); this.music = null; } }

  setMuted(m: boolean): void { this.muted = m; if (this.master) this.master.gain.value = m ? 0 : 0.6; try { localStorage.setItem('cab.muted', m ? '1' : '0'); } catch { /* ignore */ } }

  engine(speedFrac: number, nitro: boolean): void {
    if (!this.ctx || !this.engineOsc || !this.engineGain) return;
    const t = this.ctx.currentTime;
    this.engineOsc.frequency.setTargetAtTime(55 + speedFrac * 110 + (nitro ? 40 : 0), t, 0.08);
    this.engineGain.gain.setTargetAtTime(0.05 + speedFrac * 0.09, t, 0.1);
  }

  private blip(freq: number, dur: number, type: OscillatorType = 'square', gain = 0.25, slide = 0): void {
    const ctx = this.ensure(); if (!ctx || !this.master) return;
    const o = ctx.createOscillator(); const g = ctx.createGain(); o.type = type; o.frequency.value = freq;
    if (slide) o.frequency.exponentialRampToValueAtTime(Math.max(30, freq + slide), ctx.currentTime + dur);
    g.gain.setValueAtTime(gain, ctx.currentTime); g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + dur);
    o.connect(g); g.connect(this.master); o.start(); o.stop(ctx.currentTime + dur);
  }
  private noise(dur: number, gain = 0.3, freq = 800): void {
    const ctx = this.ensure(); if (!ctx || !this.master) return;
    const buf = ctx.createBuffer(1, Math.ceil(ctx.sampleRate * dur), ctx.sampleRate); const d = buf.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / d.length);
    const s = ctx.createBufferSource(); s.buffer = buf; const f = ctx.createBiquadFilter(); f.type = 'lowpass'; f.frequency.value = freq;
    const g = ctx.createGain(); g.gain.value = gain; s.connect(f); f.connect(g); g.connect(this.master); s.start();
  }

  horn(): void { if (!this.play('horn')) { this.blip(330, 0.35, 'sawtooth', 0.3); this.blip(415, 0.35, 'sawtooth', 0.25); } }
  crash(impact: number): void { if (!this.play(impact > 12 ? 'crash_big' : 'crash_small', Math.min(1, impact / 15), 250)) this.noise(0.25, Math.min(0.6, impact / 25), 600); }
  hit(): void { if (!this.play('ped_yelp', 0.7, 300)) this.blip(120, 0.12, 'triangle', 0.3, -60); }
  explode(): void { if (!this.play('explode', 1)) { this.noise(0.9, 0.7, 300); this.blip(80, 0.8, 'sawtooth', 0.4, -60); } }
  pickup(): void { if (!this.play('pickup')) { this.blip(660, 0.12, 'sine', 0.3); setTimeout(() => this.blip(880, 0.16, 'sine', 0.3), 90); } }
  cash(): void { if (!this.play('cash')) { this.blip(880, 0.1, 'square', 0.2); setTimeout(() => this.blip(1175, 0.1, 'square', 0.2), 80); setTimeout(() => this.blip(1568, 0.25, 'square', 0.25), 160); } }
  fail(): void { if (!this.play('fail')) this.blip(300, 0.5, 'sawtooth', 0.3, -200); }
  tick(): void { if (!this.play('tick')) this.blip(700, 0.1, 'square', 0.2); }
  go(): void { if (!this.play('go')) this.blip(1046, 0.4, 'square', 0.3); }
  rush(): void { if (!this.play('rush')) for (let i = 0; i < 4; i++) setTimeout(() => this.blip(700 + i * 150, 0.15, 'square', 0.25), i * 120); }
  reroll(): void { if (!this.play('reroll')) { this.blip(500, 0.08, 'triangle', 0.25); setTimeout(() => this.blip(620, 0.08, 'triangle', 0.25), 70); } }
  siren(): void { if (!this.play('siren', 0.6, 2000)) for (let i = 0; i < 6; i++) setTimeout(() => this.blip(i % 2 ? 520 : 780, 0.3, 'sawtooth', 0.2), i * 300); }
  alarm(): void { if (!this.play('alarm', 0.7, 3000)) this.siren(); }
  disaster(kind: string): void { this.play(kind, 0.8, 4000); }
  stop(): void { if (this.engineGain && this.ctx) this.engineGain.gain.setTargetAtTime(0, this.ctx.currentTime, 0.2); this.stopMusic(); }
}
